import { ReactNode, useEffect, useState } from "react";

import { Column, SCROLL_WIDTH } from "@bridgesplit/ui";
import {
    useSyncLoopscaleWallet,
    useOracleMarketInformationsQuery,
    useIsSeeker,
    usePausedExternalYieldSources
} from "@bridgesplit/abf-react";
import { useMaintainCssTheme } from "@bridgesplit/react";
import { getFeatureFlag } from "@bridgesplit/utils";

import AppAlertBanner from "./AppAlertBanner";
import TransactionsHaltedBanner from "./TransactionsHaltedBanner";
import { useSetMixpanel, useSentryUser } from "../../hooks";

export default function AppWrapper({ children }: { children: ReactNode }) {
    // Sentry user tracking
    useSentryUser();

    //mixpanel setup
    useSetMixpanel();

    // wallet store sync
    useSyncLoopscaleWallet();

    // Detect PWA access early via ?source=pwa and persist to localStorage
    useIsSeeker();

    // Prefetch paused external yield sources on app load so quote filters can exclude them.
    usePausedExternalYieldSources();

    // Prefetch all market information (62 markets) into oracleMarketInformationsCache.
    // Deferred until after first paint so the payload doesn't block TTI.
    // `includeAssetInfo: false` slims this to identity + underlyingAssets (~85% smaller); the heavy
    // per-asset risk/oracle terms are fetched scoped on demand by usePresetMarketInfos / useMarketOracleInfos.
    const prefetchMarketInfos = useDeferredFlag();
    useOracleMarketInformationsQuery(
        { principalMints: [], addresses: [], includeAssetInfo: false },
        { skip: getFeatureFlag("currentlyDownForMaintenance") || !prefetchMarketInfos }
    );

    useMaintainCssTheme();

    return (
        <Column
            id="app-wrapper"
            sx={{
                height: "100%",
                minHeight: "100vh",
                width: `calc(100vw - ${SCROLL_WIDTH})`
            }}
        >
            <AppAlertBanner />
            <TransactionsHaltedBanner />
            {children}
        </Column>
    );
}

function useDeferredFlag() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const ric =
            typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
                ? window.requestIdleCallback
                : (cb: () => void) => window.setTimeout(cb, 0);
        const cic =
            typeof window !== "undefined" && typeof window.cancelIdleCallback === "function"
                ? window.cancelIdleCallback
                : window.clearTimeout;
        const handle = ric(() => setReady(true));
        return () => cic(handle as number);
    }, []);
    return ready;
}
